import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lessonService, noteService, chatService } from '../services/api';
import SideMenu from './SideMenu';
import { useTheme } from '../ThemeContext';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-xml-doc';
import './SideMenu.css';
import './Lesson.css';
import { Play, Pause, X, Edit2, Edit, MessageCircle, AlertTriangle } from 'react-feather';

const Lesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef(null);
  const [audioError, setAudioError] = useState(null);
  const [randomHeights, setRandomHeights] = useState([]);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isContentGenerating, setIsContentGenerating] = useState(false);
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState(null);
  const [popupImageCaption, setPopupImageCaption] = useState('');
  
  // Text selection related states
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState({ x: 0, y: 0 });
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const contentRef = useRef(null);



  // Add state for Ask AI button and chat panel
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showAskAIHelp, setShowAskAIHelp] = useState(true);
  const [chatMessages, setChatMessages] = useState([
    { role: 'system', content: 'Ask me anything about this lesson!' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatMessagesEndRef = useRef(null);

  // Add keyframes for spinner animation
  const spinnerAnimation = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  // Add a debug mode state
  const [debugMode] = useState(false);

  // Add state for note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');

  // Add state for rate limit notification - remove modal states
  const [rateLimitError, setRateLimitError] = useState(null);

  // Add state for assessment generation tracking
  const [isAssessmentGenerating, setIsAssessmentGenerating] = useState(false);
  const [assessmentGenerationStarted, setAssessmentGenerationStarted] = useState(false);
  const [showAssessmentStatus, setShowAssessmentStatus] = useState(false);
  // Helper: robust extract of JSON from text (similar to backend)
  const extractJsonFromTextClient = (text) => {
    if (!text || typeof text !== 'string') return '';
    try {
      // Remove code fences if present (permit optional spaces/case)
      let t = text.replace(/```\s*json/gi, '').replace(/```/g, '').trim();
      const start = t.indexOf('{');
      const end = t.lastIndexOf('}');
      if (start !== -1 && end > start) {
        return t.substring(start, end + 1);
      }
      return '';
    } catch {
      return '';
    }
  };

  // Try to coerce loosely formatted JSON (unescaped backslashes, trailing commas) into valid JSON
  const tryParseLooseJson = (raw) => {
    const jsonBody = extractJsonFromTextClient(raw) || raw;
    // Remove trailing commas before ] or }
    let t = jsonBody.replace(/,\s*([}\]])/g, '$1');
    // Escape single backslashes inside quoted strings
    let result = '';
    let inStr = false;
    let esc = false;
    for (let i = 0; i < t.length; i++) {
      const ch = t[i];
      if (!inStr) {
        if (ch === '"') { inStr = true; result += ch; esc = false; }
        else { result += ch; }
        continue;
      }
      // inside string
      if (esc) { result += ch; esc = false; continue; }
      if (ch === '\\') { result += '\\\\'; continue; }
      if (ch === '"') { inStr = false; result += ch; continue; }
      if (ch === '\\') { esc = true; result += ch; continue; }
      result += ch;
    }
    try {
      return JSON.parse(result);
    } catch {
      try { return JSON.parse(jsonBody); } catch { return null; }
    }
  };

  // legacy HTML renderer removed; using React AST renderer instead

  // Helper: detect if lesson.content has image placeholder (AST JSON only)
  const parseAstStrictOrDoubleEncoded = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    let t = raw.trim();
    // 0) Strip code fences if present
    t = t.replace(/```\s*json/gi, '').replace(/```/g, '').trim();
    try {
      // If content is a quoted JSON string, first parse to get inner string
      if (t.startsWith('"') && t.endsWith('"')) {
        const inner = JSON.parse(t);
        if (typeof inner === 'string') t = inner;
        else return inner; // already object
      }
    } catch { /* ignore */ }

    // Try direct parse of the (possibly de-quoted) string
    try {
      const ast = JSON.parse(t);
      if (ast && Array.isArray(ast.blocks)) return ast;
      // Handle PascalCase keys persisted by backend in older versions
      if (ast && Array.isArray(ast.Blocks)) {
        return normalizeAstCase(ast);
      }
    } catch { /* ignore */ }

    // Try extracting braces and parsing
    try {
      const body = extractJsonFromTextClient(t) || t;
      const ast = JSON.parse(body);
      if (ast && Array.isArray(ast.blocks)) return ast;
      if (ast && Array.isArray(ast.Blocks)) return normalizeAstCase(ast);
    } catch { /* ignore */ }

    // Fall back to loose parser
    try {
      // Attempt a repair: unescape common sequences from double-encoded strings
      let repaired = t
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\');
      let ast = null;
      try { ast = JSON.parse(repaired); } catch {}
      if (!(ast && Array.isArray(ast.blocks))) {
        ast = tryParseLooseJson(t);
      }
      if (ast && Array.isArray(ast.blocks)) return ast;
      if (ast && Array.isArray(ast.Blocks)) return normalizeAstCase(ast);
    } catch { /* ignore */ }
    return null;
  };

  // Normalize AST objects that use PascalCase property names to the lowercase
  // schema the renderer expects
  const normalizeAstCase = (ast) => {
    const norm = {
      title: ast.title || ast.Title || '',
      estimationMinutes: ast.estimationMinutes || ast.EstimationMinutes || 30,
      imagePrompt: ast.imagePrompt || ast.ImagePrompt || '',
      blocks: [],
      resources: ast.resources || ast.Resources || {}
    };
    const srcBlocks = Array.isArray(ast.blocks) ? ast.blocks : Array.isArray(ast.Blocks) ? ast.Blocks : [];
    norm.blocks = srcBlocks.map((b) => ({
      type: (b.type || b.Type || '').toString(),
      level: b.level ?? b.Level ?? undefined,
      text: b.text || b.Text || '',
      latex: b.latex || b.Latex || '',
      language: b.language || b.Language || '',
      code: b.code || b.Code || '',
      ordered: b.ordered ?? b.Ordered ?? undefined,
      items: b.items || b.Items || [],
      alt: b.alt || b.Alt || '',
      spec: b.spec || b.Spec || null,
    }));
    return norm;
  };

  const hasImagePlaceholderInContent = (content) => {
    const ast = parseAstStrictOrDoubleEncoded(content);
    return !!(ast && Array.isArray(ast.blocks) && ast.blocks.some(b => (b.type || '').toLowerCase() === 'imageplaceholder'));
  };

  // AST-only rendering helpers
  const parseAst = useMemo(() => {
    if (!lesson?.content) return null;
    const raw = (lesson.content || '').trim();
    const ast = parseAstStrictOrDoubleEncoded(raw);
    return ast;
  }, [lesson?.content]);

  // Helper function to strip LaTeX delimiters from math content
  const stripMathDelimiters = (latex) => {
    if (!latex || typeof latex !== 'string') return '';
    
    let content = latex.trim();
    
    // Remove inline math delimiters: \( ... \) or $ ... $
    if (content.startsWith('\\(') && content.endsWith('\\)')) {
      content = content.slice(2, -2);
    } else if (content.startsWith('$') && content.endsWith('$') && content.length > 2) {
      // Only strip single $ if it's not display math ($$)
      if (!content.startsWith('$$')) {
        content = content.slice(1, -1);
      }
    }
    
    // Remove display math delimiters: \[ ... \] or $$ ... $$
    if (content.startsWith('\\[') && content.endsWith('\\]')) {
      content = content.slice(2, -2);
    } else if (content.startsWith('$$') && content.endsWith('$$')) {
      content = content.slice(2, -2);
    }
    
    return content.trim();
  };

  const MathInline = ({ latex }) => {
    try {
      const cleanLatex = stripMathDelimiters(latex || '');
      const html = katex.renderToString(cleanLatex, { displayMode: false, throwOnError: false, strict: false });
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (error) { 
      console.warn('KaTeX inline error:', error, 'Original latex:', latex);
      return <span className="math-error">Math error</span>; 
    }
  };

  const MathBlock = ({ latex }) => {
    try {
      const cleanLatex = stripMathDelimiters(latex || '');
      const html = katex.renderToString(cleanLatex, { displayMode: true, throwOnError: false, strict: false });
      return <div className="katex-display" dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (error) { 
      console.warn('KaTeX block error:', error, 'Original latex:', latex);
      return <div className="math-error">Math error</div>; 
    }
  };

  const CodeBlock = ({ language = 'plaintext', code = '' }) => {
    const lang = (language || 'plaintext').toLowerCase();
    let highlighted = code;
    try {
      const prismLang = Prism.languages[lang] || Prism.languages.plaintext;
      highlighted = Prism.highlight(code, prismLang, lang);
    } catch {}
    return (
      <pre className={`language-${lang}`}><code className={`language-${lang}`} dangerouslySetInnerHTML={{ __html: highlighted }} /></pre>
    );
  };

  const ImagePlaceholder = ({ alt }) => {
    // If an image was already generated, display it
    if (lesson?.imageUrl) {
      const caption = alt || '';
      let imageUrl = lesson.imageUrl;
      if (!imageUrl.startsWith('http')) {
        imageUrl = `https://nd69zr.realhost-free.net${imageUrl}`;
      }
      return (
        <div className="lesson-image-container">
          <img
            src={imageUrl}
            alt={caption}
            className="lesson-image"
            data-image-url={lesson.imageUrl}
            data-image-caption={caption}
          />
          {caption ? <div className="lesson-image-caption">{caption}</div> : null}
        </div>
      );
    }

    // Only show the placeholder if we have an image prompt to generate from
    const hasImagePrompt = (lesson?.imagePrompt || '').trim() !== '';
    if (!hasImagePrompt) {
      return null;
    }
    return (
      <div className={`lesson-image-container ${isImageGenerating ? 'image-loading' : 'image-placeholder'}`}>
        {isImageGenerating ? (
          <>
            <div className="image-loading-spinner"></div>
            <div className="image-loading-text">Generating image{alt ? `: ${alt}` : ''}</div>
          </>
        ) : (
          <>
            <div className="image-placeholder-icon">🖼️</div>
            {alt ? <div className="image-placeholder-text">{alt}</div> : null}
            <button className="generate-image-btn" onClick={handleGenerateImage} data-generate-image>Generate Image</button>
          </>
        )}
      </div>
    );
  };

  const ASTBlock = ({ block }) => {
    const type = (block?.type || '').toLowerCase();
    switch (type) {
      case 'header': {
        const level = Math.min(Math.max(block.level || 2, 1), 4);
        const Tag = `h${level}`;
        return <Tag className="lesson-section">{block.text}</Tag>;
      }
      case 'paragraph':
        return <p>{block.text}</p>;
      case 'list': {
        const items = Array.isArray(block.items) ? block.items : [];
        return block.ordered ? (
          <ol>{items.map((it, i) => <li key={i}>{it}</li>)}</ol>
        ) : (
          <ul>{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
        );
      }
      case 'codeblock':
      case 'codeBlock':
        return <CodeBlock language={block.language} code={block.code} />;
      case 'mathinline':
      case 'mathInline':
        return <MathInline latex={block.latex} />;
      case 'mathblock':
      case 'mathBlock':
        return <MathBlock latex={block.latex} />;
      case 'imageplaceholder':
      case 'imagePlaceholder':
        return <ImagePlaceholder alt={block.alt} />;
      case 'chart':
        return <div data-chart="vega-lite" data-spec={JSON.stringify(block.spec || {})}></div>;
      default:
        if (block?.text) return <p>{block.text}</p>;
        return null;
    }
  };

  // Add scroll event listener to hide Ask AI help text on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (showAskAIHelp && window.scrollY > 50) {
        setShowAskAIHelp(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showAskAIHelp]);

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (isChatOpen) {
      chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isChatOpen]);

  // Add new effect to check for audio URL when lesson loads
  useEffect(() => {
    const checkAndLoadAudio = async () => {
      if (!lesson?.id) return;
      
      try {
        // Only make the API call if we know there's audio available
        if (lesson.hasAudio) {
          setIsAudioLoading(true);
          const audioData = await lessonService.getLessonAudio(lesson.id);
          if (audioData && audioData.audioUrl) {
            setAudioUrl(audioData.audioUrl);
          }
          setIsAudioLoading(false);
        } else {
          setIsAudioLoading(false);
        }
      } catch (error) {
        console.error('Failed to check audio status:', error);
        setAudioError('Failed to load audio');
        setIsAudioLoading(false);
      }
    };

    if (lesson && !audioUrl) {
      checkAndLoadAudio();
    }
  }, [lesson]);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        if (!lessonId) {
          setError('No lesson ID provided');
          setLoading(false);
          return;
        }
        
        const lessonData = await lessonService.getLesson(lessonId);
        
        if (!lessonData) {
          setError('Lesson not found');
          setLoading(false);
          return;
        }
        
        setLesson(lessonData);
        console.log("Lesson Data for lesson", lessonData);
        
        // Start fire-and-forget assessment generation if lesson has content
        if (lessonData.content && lessonData.content.trim() !== '' && !assessmentGenerationStarted) {
          setAssessmentGenerationStarted(true);
          lessonService.startAssessmentGeneration(lessonId);
        }
        
        // If the lesson doesn't have content, generate it
        if (!lessonData.content || lessonData.content.trim() === '') {
          try {
            console.log(`Lesson ${lessonId} has no content. Generating content...`);
            
            // Add a flag to prevent duplicate content generation API calls
            if (!window.generatingContent) {
              window.generatingContent = true;
              setIsContentGenerating(true);
              
              try {
                const updatedLesson = await lessonService.generateLessonContent(lessonId);
                window.generatingContent = false;
                setIsContentGenerating(false);
                setLesson(updatedLesson);
                
                // Automatically generate image after content is generated
                const hasImagePlaceholder = hasImagePlaceholderInContent(updatedLesson.content);
                const hasPrompt = (updatedLesson.imagePrompt || '').trim() !== '';
                if (hasImagePlaceholder && hasPrompt && !updatedLesson.imageUrl) {
                  console.log(`Automatically generating image for lesson ${lessonId}`);
                  setIsImageGenerating(true);
                  try {
                    const imageResult = await lessonService.generateLessonImage(lessonId);
                    if (imageResult && imageResult.imageUrl) {
                      setLesson(prev => ({
                        ...prev,
                        imageUrl: imageResult.imageUrl
                      }));
                    }
                  } catch (imgError) {
                    console.error('Error generating image:', imgError);
                  } finally {
                    setIsImageGenerating(false);
                  }
                }
                
                // Start fire-and-forget assessment generation after content is generated
                if (!assessmentGenerationStarted) {
                  setAssessmentGenerationStarted(true);
                  lessonService.startAssessmentGeneration(lessonId);
                }
              } catch (contentError) {
                window.generatingContent = false;
                setIsContentGenerating(false);
                
                // Handle rate limit error specifically
                if (contentError.isRateLimit) {
                  console.log('Rate limit error detected:', contentError.details);
                  setRateLimitError(contentError.details);
                } else {
                  console.error('Failed to generate lesson content:', contentError);
                  setError('Failed to generate lesson content. Please try again later.');
                }
              }
            } else {
              console.log('Content generation already in progress, skipping duplicate call');
            }
            
            console.log(`Content generated successfully for lesson ${lessonId}`);
          } catch (genError) {
            window.generatingContent = false;
            setIsContentGenerating(false);
            console.error('Failed to generate lesson content:', genError);
          }
        } 
        // If lesson has content with image placeholder but no image, automatically generate it
        else if (lessonData.content && hasImagePlaceholderInContent(lessonData.content) && (lessonData.imagePrompt || '').trim() !== '' && !lessonData.imageUrl) {
          console.log(`Lesson ${lessonId} has content but no image. Generating image...`);
          setIsImageGenerating(true);
          try {
            const imageResult = await lessonService.generateLessonImage(lessonId);
            if (imageResult && imageResult.imageUrl) {
              setLesson(prev => ({
                ...prev,
                imageUrl: imageResult.imageUrl
              }));
            }
          } catch (imgError) {
            console.error('Error generating image:', imgError);
          } finally {
            setIsImageGenerating(false);
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load lesson: ' + (err.message || 'Unknown error'));
        setLoading(false);
        console.error('Error loading lesson:', err);
      }
    };

    fetchLesson();
  }, [lessonId]);

  // Function to dismiss rate limit error
  const dismissRateLimitError = () => {
    setRateLimitError(null);
  };

  // Update audio loading state handling
  useEffect(() => {
    if (audioUrl) {
      // Don't set isAudioLoading to false here
      // It will be set to false when audio is loaded in handleLoadedMetadata
      setAudioError(null);
    }
  }, [audioUrl]);

  // Generate random heights for equalizer bars
  useEffect(() => {
    const heights = [...Array(60)].map(() => Math.floor(Math.random() * 20) + 5);
    setRandomHeights(heights);
    
    // Regenerate heights every 3 seconds for a dynamic effect when playing
    if (isPlaying) {
      const interval = setInterval(() => {
        setRandomHeights([...Array(60)].map(() => Math.floor(Math.random() * 20) + 5));
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  const parseJsonString = (jsonString) => {
    if (!jsonString || typeof jsonString !== 'string') {
      return {};
    }
    
    // Check if the string looks like JSON (starts with { or [)
    const trimmed = jsonString.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      console.error('Not a valid JSON string format:', trimmed.substring(0, 30) + '...');
      return {};
    }
    
    try {
      return JSON.parse(jsonString);
    } catch (err) {
      console.error('Failed to parse JSON string', err);
      return {};
    }
  };

  const handleCompleteLesson = async () => {
    try {
      // Check if assessment generation is in progress
      if (lessonService.isAssessmentGenerating(lessonId)) {
        setIsAssessmentGenerating(true);
        console.log('Assessment generation in progress, waiting for completion...');
        
        try {
          // Wait for assessment generation to complete
          await lessonService.waitForAssessmentGeneration(lessonId);
          console.log('Assessment generation completed, proceeding to assessment page');
        } catch (error) {
          console.error('Assessment generation timeout or failed:', error);
          // Continue to assessment page anyway - it will handle generation there
        } finally {
          setIsAssessmentGenerating(false);
        }
      }
      
      navigate(`/lessons/${lessonId}/assessment`);

    } catch (err) {
      setIsAssessmentGenerating(false);
      const errorMessage = err.response?.data || 'Failed to load assessment. Please try again later.';
      setError(errorMessage);
      console.error('Error loading assessment:', err);
      
      // Allow the user to see the error for a few seconds before clearing it
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  // Helper function to handle image generation
  const handleGenerateImage = async () => {
    // Require a valid prompt (server will also validate)
    const hasPrompt = (lesson?.imagePrompt || '').trim() !== '';
    if (!lesson?.id || lesson.imageUrl || !hasPrompt) {
      return;
    }
    
    try {
      setIsImageGenerating(true);
      const result = await lessonService.generateLessonImage(lesson.id);
      
      if (result && result.imageUrl) {
        setLesson(prev => ({
          ...prev,
          imageUrl: result.imageUrl
        }));
      }
    } catch (error) {
      console.error('Failed to generate image:', error);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsImageGenerating(false);
    }
  };

  // Function to open image popup
  const openImagePopup = (imageUrl, caption = '') => {
    setPopupImageUrl(imageUrl);
    setPopupImageCaption(caption);
    setIsImagePopupOpen(true);
    // Prevent scrolling when popup is open
    document.body.style.overflow = 'hidden';
  };

  // Function to close image popup
  const closeImagePopup = () => {
    setIsImagePopupOpen(false);
    setPopupImageUrl(null);
    setPopupImageCaption('');
    // Restore scrolling when popup is closed
    document.body.style.overflow = 'auto';
  };

  // legacy HTML hydration removed; charts are embedded separately

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsAudioLoading(false); // Set loading to false only when audio is actually loaded
    }
  };

  const handleAudioError = (e) => {
    console.error('Audio error:', e);
    setAudioError('Failed to load audio');
    setIsAudioLoading(false);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    
    const waveformElement = e.currentTarget;
    const rect = waveformElement.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const width = rect.width;
    
    // Calculate percentage clicked
    const percentage = offsetX / width;
    const seekTime = percentage * duration;
    
    // Set current time of audio to the seek time
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleMouseMove = (e) => {
    if (!audioRef.current || !duration) return;
    
    const waveformElement = e.currentTarget;
    const rect = waveformElement.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const width = rect.width;
    
    // Calculate time based on hover position
    const percentage = offsetX / width;
    const hoverTime = percentage * duration;
    
    setHoverPosition(percentage);
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setHoverPosition(null);
  };

  const handleGenerateAudio = async () => {
    if (!lesson?.content) return;
    
    setIsAudioLoading(true);
    setAudioError(null);
    try {
      const audioData = await lessonService.generateLessonAudio(lesson.id);
      if (audioData && audioData.audioUrl) {
        setAudioUrl(audioData.audioUrl);
      }
      setIsAudioLoading(false);
    } catch (error) {
      console.error('Failed to generate audio:', error);
      setError('Failed to generate audio version of the lesson');
      setAudioError('Generation failed');
      setIsAudioLoading(false);
    }
  };

  // Add event listener for the generate image button click
  useEffect(() => {
    const handleGenerateImageEvent = (e) => {
      if (e.detail.lessonId === parseInt(lessonId)) {
        handleGenerateImage();
      }
    };
    
    document.addEventListener('generateImage', handleGenerateImageEvent);
    
    return () => {
      document.removeEventListener('generateImage', handleGenerateImageEvent);
    };
  }, [lessonId, lesson]);

  // Click handler for inline generate image buttons inside placeholders
  useEffect(() => {
    if (!contentRef.current) return;
    const container = contentRef.current;
    const onClick = (e) => {
      const btn = e.target.closest('[data-generate-image]');
      if (btn) {
        e.preventDefault();
        handleGenerateImage();
      }
    };
    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [contentRef.current, lesson?.id]);

  // Effect to add click handlers to content images
  useEffect(() => {
    const addImageClickHandlers = () => {
      const contentImages = document.querySelectorAll('.content-text img, .lesson-image');
      contentImages.forEach(img => {
        if (!img.hasAttribute('data-has-click-handler')) {
          img.style.cursor = 'pointer';
          img.addEventListener('click', () => {
            let caption = '';
            let imageUrl = img.src;
            
            // First check if the image has data attributes (for generated images)
            if (img.hasAttribute('data-image-caption')) {
              caption = img.getAttribute('data-image-caption');
            } else {
              // Try to find caption if the image is inside a figure with figcaption
              const parent = img.closest('figure, .lesson-image-container');
              if (parent) {
                const captionElem = parent.querySelector('figcaption, .lesson-image-caption');
                if (captionElem) {
                  caption = captionElem.textContent;
                }
              }
              
              // If no figcaption found, use alt text as caption
              if (!caption && img.alt) {
                caption = img.alt;
              }
            }
            
            // Use data-image-url if available (for cache busting)
            if (img.hasAttribute('data-image-url')) {
              const dataUrl = img.getAttribute('data-image-url');
              // Add cache busting parameter if it's a local URL
              if (dataUrl.startsWith('https://nd69zr.realhost-free.net') && !dataUrl.includes('?')) {
                imageUrl = `${dataUrl}?t=${new Date().getTime()}`;
              } else {
                imageUrl = dataUrl;
              }
            }
            
            openImagePopup(imageUrl, caption);
          });
          img.setAttribute('data-has-click-handler', 'true');
        }
      });
    };

    // Add handlers after content is rendered
    if (lesson?.content && !isContentGenerating) {
      // Short timeout to ensure DOM is updated
      setTimeout(addImageClickHandlers, 100);
    }
  }, [lesson?.content, isContentGenerating]);

  // Wrap handleTextSelection in useCallback to prevent infinite dependency cycle
  const handleTextSelection = useCallback(() => {
    // Remove highlighting mode check
    const selection = window.getSelection();
    
    if (selection.toString().trim().length > 0) {
      // Get selected text
      const text = selection.toString().trim();
      setSelectedText(text);
      
      // Calculate position for the selection menu
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      // Get accurate scroll position
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
      
      // Position the menu under the selected text, centered horizontally
      const x = rect.left + scrollLeft + (rect.width / 2) - 250; // Center of selection horizontally
      const y = rect.bottom + scrollTop - 45; // Below the selection with 10px gap
      
      setSelectionPosition({ x, y });
      setShowSelectionMenu(true);
    } else {
      // Remove highlighting mode check
      setShowSelectionMenu(false);
    }
  }, []);
  
  // Update the mouseup event listener 
  useEffect(() => {
    const handleDocumentMouseUp = (e) => {
      // Skip if click is inside menus to prevent interference
      const selectionMenuElement = document.querySelector('.selection-menu');
      
      if (selectionMenuElement && selectionMenuElement.contains(e.target)) return;
      
      // Process selection after slight delay
      setTimeout(handleTextSelection, 10);
    };
    
    document.addEventListener('mouseup', handleDocumentMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [handleTextSelection]); // Update dependencies

  // Fix click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the selection menu
      const selectionMenuElement = document.querySelector('.selection-menu');
      
      const isClickInsideSelectionMenu = selectionMenuElement && selectionMenuElement.contains(event.target);
      
      // Only close if click is outside
      if (!isClickInsideSelectionMenu) {
        // Close menu and reset state
        setShowSelectionMenu(false);
        
        // Only clear selection if not clicking on a button with preventDefault
        if (!event.defaultPrevented) {
          setSelectedText('');
        }
      }
    };
    
    // Use mousedown to catch clicks before they affect selection
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to create a note from selected text
  const createNote = () => {
    if (!selectedText || !lesson?.id) return;
    
    // Set initial content with the selected text
    setNoteContent(selectedText);
    // Generate a default title based on the first few words of selected text
    const defaultTitle = selectedText.length > 30 
      ? selectedText.substring(0, 30) + '...' 
      : selectedText;
    setNoteTitle(defaultTitle);
    
    // Show note form and hide selection menu
    setShowNoteForm(true);
    setShowSelectionMenu(false);
  };
  
  // Function to handle saving the note
  const handleSaveNote = async () => {
    if (!noteTitle.trim()) {
      alert('Note title is required');
      return;
    }

    try {
      await noteService.createNote(
        noteTitle, 
        noteContent, 
        lesson.id
      );
      
      // Show a temporary success message
      alert('Note created successfully!');
      
      // Reset form state
      setShowNoteForm(false);
      setNoteTitle('');
      setNoteContent('');
      setSelectedText('');
      window.getSelection().removeAllRanges();
    } catch (error) {
      console.error('Failed to create note:', error);
      setError('Failed to create note. Please try again.');
    }
  };

  // Function to cancel note creation
  const handleCancelNote = () => {
    setShowNoteForm(false);
    setNoteTitle('');
    setNoteContent('');
  };
  
  // Function to ask LLM about selected text
  const askLLM = () => {
    if (!selectedText) return;
    
    // Open the chat panel and set the input with the selected text
    if (!isChatOpen) {
      setIsChatOpen(true);
    }
    
    // Set the chat input to the selected text with "Explain this: " prefix
    setChatInput(`Explain this: "${selectedText}"`);
    
    // Reset selection state
    setShowSelectionMenu(false);
    setSelectedText('');
    window.getSelection().removeAllRanges();
  };

  // Chat functions
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    
    if (!chatInput.trim()) return;
    
    // Add user message to chat
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    
    // Clear input
    setChatInput('');
    
    // Show loading state
    setIsChatLoading(true);
    
    try {
      // Add lesson context to the message for better responses
      const contextMessage = `This question is about this lesson: ${lesson?.title || ''}`;
      
      // Send message to the LLM API
      const response = await chatService.sendMessage(`${contextMessage}\n\n${userMessage.content}`);
      
      // Add AI response to chat
      if (response && response.response) {
        const aiResponse = { 
          role: 'assistant', 
          content: response.response 
        };
        
        setChatMessages(prev => [...prev, aiResponse]);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your request. Please try again later.'
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Handle Enter key to send chat message
  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle chat panel
  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  // Function to handle the clear chat button
  const handleClearChat = () => {
    setChatMessages([
      { role: 'system', content: 'Ask me anything about this lesson!' }
    ]);
  };

  // Effect to monitor assessment generation status
  useEffect(() => {
    if (!assessmentGenerationStarted || !lessonId) return;
    
    const checkAssessmentStatus = () => {
      const isGenerating = lessonService.isAssessmentGenerating(lessonId);
      setShowAssessmentStatus(isGenerating && !isAssessmentGenerating);
    };
    
    // Check immediately
    checkAssessmentStatus();
    
    // Check every 2 seconds
    const interval = setInterval(checkAssessmentStatus, 2000);
    
    return () => clearInterval(interval);
  }, [assessmentGenerationStarted, lessonId, isAssessmentGenerating]);

  if (loading) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`lesson-main-content ${theme}`}>
          <div className="lesson-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">📚</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <style>{spinnerAnimation}</style>
              <p className="sb-loading-text">Loading lesson...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const videoUrls = parseJsonString(lesson?.videoUrls || '{}');
  const resourceLinks = parseJsonString(lesson?.resourceLinks || '{}');
  const videosList = (videoUrls && (videoUrls.videos || videoUrls.Videos)) || [];
  const articlesList = (resourceLinks && (resourceLinks.articles || resourceLinks.Articles)) || [];
  const booksList = (resourceLinks && (resourceLinks.books || resourceLinks.Books)) || [];

  return (
    <div className="app-layout" style={{minHeight: '95vh'}}>
      <SideMenu />
      <div className={`lesson-main-content ${theme} ${isChatOpen ? 'chat-open' : ''}`}>
        
        <div className={`lesson-container ${theme}`} style={{textAlign: 'left', paddingLeft: '30px'}}>
          {error && <div className="error-message">{error}</div>}
          
          <div className={`page-header ${theme}`}>
            <h1 className="lesson-title">{lesson?.title}</h1>
            { /* lesson?.estimatedTimeInMinutes && (
              <div className="lesson-estimated-time">
                <svg className="time-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Estimated time: {lesson.estimatedTimeInMinutes} minutes</span>
              </div>
            )} */}
          </div>



          {/* Audio Section - Hide when generating content or no content */}
          {!isContentGenerating && lesson?.content && (
            <div className={`audio-section ${theme}`}>
              <div className="audio-header">
                <svg className="audio-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3v18M8 7v10M16 7v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <h3 className="audio-title">Listen to this lesson</h3>
              </div>
              
              {/* Always show the audio player if we have an audioUrl, even while loading */}
              {(audioUrl || isAudioLoading) ? (
                <>
                  <div className={`audio-player ${theme}`}>
                    <button 
                      className="play-button"
                      onClick={handlePlayPause}
                      disabled={isAudioLoading || audioError}
                    >
                      {isAudioLoading ? (
                        <div className="lesson-audio-loading-spinner" />
                      ) : audioError ? (
                        <div className="audio-error-icon-container" title={audioError}>
                          <AlertTriangle size={20} />
                        </div>
                      ) : (
                        <div className={`icon-container ${isPlaying ? 'playing' : ''}`}>
                          {isPlaying ? (
                            <Pause size={24} fill="white" />
                          ) : (
                            <Play size={24} fill="white" />
                          )}
                        </div>
                      )}
                    </button>
                    
                    <div 
                      className="waveform" 
                      onClick={handleSeek}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={handleMouseLeave}
                    >
                      {isAudioLoading ? (
                        <div className="audio-loading-message">Loading audio</div>
                      ) : audioError ? (
                        <div className="audio-error-message">{audioError}</div>
                      ) : (
                        <>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar" 
                              style={{ width: `${(currentTime / duration) * 100}%` }}
                            ></div>
                            {isHovering && (
                              <div 
                                className="hover-indicator"
                                style={{ left: `${hoverPosition * 100}%` }}
                              >
                                <div className="hover-time">
                                  {formatTime(hoverPosition * duration)}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className={`wave-bars-container ${isPlaying ? 'playing-animation' : ''}`}>
                            {[...Array(60)].map((_, i) => {
                              const barPosition = (i + 1) / 60;
                              const playbackPosition = duration > 0 ? currentTime / duration : 0;
                              const isActive = barPosition <= playbackPosition;
                              
                              return (
                                <div 
                                  key={i} 
                                  className={`wave-bar ${isActive ? 'active' : 'inactive'}`}
                                  style={{ 
                                    '--random-height': `${randomHeights[i] || 10}px`,
                                    animationDelay: `${i * 0.03}s`
                                  }}
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="time-display">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </div>
                  </div>
                  
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    onError={handleAudioError}
                    style={{ display: 'none' }}
                  />
                </>
              ) : (
                // Show generate button only when there's no audio and not generating
                <button 
                  className="generate-audio-btn"
                  onClick={handleGenerateAudio}
                  disabled={isAudioLoading}
                >
                  Generate Audio Version
                </button>
              )}
            </div>
          )}

          <div className="content-text" ref={contentRef}>
            {isContentGenerating ? (
              <div className="lesson-loading-container" style={{ padding: '30px 0' }}>
                <div className="loading-icon-container">
                  <div className="empty-state-icon">📝</div>
                </div>
                <div className="sb-loading-row">
                  <div className="sb-loading-spinner"></div>
                  <style>{spinnerAnimation}</style>
                  <p className="sb-loading-text">Generating lesson content...</p>
                </div>
                <p className="sb-loading-subtext">
                  This may take a minute or two as we create personalized content based on your roadmap.
                </p>
              </div>
            ) : /* rate limit display disabled */ lesson?.content ? (
              <div className="lesson-parsed-content">
                {parseAst ? (
                  parseAst.blocks.map((b, idx) => <ASTBlock key={idx} block={b} />)
                ) : (
                  <p>{(lesson.content || '').slice(0, 500)}</p>
                )}
              </div>
            ) : (
              <div className="lesson-no-content-container">
                <div className="empty-state-icon">📚</div>
                <p>Content is still generating. This may take a minute or two.</p>
                <p>Try refreshing the page. If the problem persists, please contact support.
                  <br/>
                  brainyai.info@gmail.com
                </p>
                <button 
                  onClick={() => window.location.reload()}
                  className="reload-page-button"
                >
                  <span>
                    <svg className="lp-refresh-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.65 6.35A8 8 0 1 0 19.73 14h-2.08A6 6 0 1 1 16 7.89V10l4-4-4-4v2.73z" fill="currentColor" />
                    </svg>
                    Reload Page
                  </span>
                </button>
              </div>
            )}
          </div>

          {booksList && booksList.length > 0 && !isContentGenerating && lesson?.content && (
            <div className={`lesson-books ${theme}`}>
              <h3>Recommended Books</h3>
              <ul className="book-list">
                {booksList.map((book, index) => (
                  <li key={index}>
                    <strong>{(book.title || book.Title || '').toString()}</strong> by {(book.author || book.Author || '').toString()}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {!isContentGenerating && lesson?.content && (
            <div className="lesson-actions">
              <button 
                onClick={() => navigate(`/learning-plan`)} 
                className="lesson-back-button"
                style={{flex: 'none'}}
              >
                Back to Roadmap
              </button>
              {!lesson?.isCompleted && (
                <button 
                  onClick={handleCompleteLesson} 
                  className="roadmap-button"
                  style={{marginLeft: '10px'}}
                  disabled={isAssessmentGenerating}
                >
                  {isAssessmentGenerating ? (
                    <>
                      <div className="sb-loading-spinner" style={{marginRight: '8px', width: '16px', height: '16px'}}></div>
                      Preparing Assessment...
                    </>
                  ) : (
                    'Take Assessment'
                  )}
                </button>
              )}
            </div>
          )}

          {/* Assessment Generation Status Indicator */}
          {showAssessmentStatus && (
            <div className={`assessment-generation-status ${theme}`}>
              <div className="status-icon"></div>
              <span>Assessment is being prepared...</span>
            </div>
          )}

          {/* Text Selection Menu - Icons Only */}
          {showSelectionMenu && (
            <div 
              className={`selection-menu ${theme}`} 
              style={{ 
                position: 'absolute',
                left: `${selectionPosition.x}px`, 
                top: `${selectionPosition.y}px`,
                transform: 'translateX(-50%)', // Center horizontally only
                zIndex: 9999
              }}
            >
              <button 
                className={`selection-menu-button ${theme}`}
                onClick={createNote}
                data-tooltip="Create Note"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Edit size={16} />
              </button>
              
              <button 
                className={`selection-menu-button ${theme}`}
                onClick={askLLM}
                data-tooltip="Ask AI"
                onMouseDown={(e) => e.preventDefault()}
              >
                <MessageCircle size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Popup */}
      {isImagePopupOpen && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className={`image-popup-container ${theme}`}>
            <button className="image-popup-close" onClick={closeImagePopup}>×</button>
            <img className="image-popup-img" src={popupImageUrl} alt={popupImageCaption} />
            {popupImageCaption && (
              <div className={`image-popup-caption ${theme}`}>{popupImageCaption}</div>
            )}
          </div>
        </div>
      )}

      {/* Ask AI Button */}
      <div className={`ask-ai-button-container ${theme} ${isChatOpen ? 'chat-open' : ''}`}>
        <button 
          className={`ask-ai-button ${isChatOpen ? 'active' : ''}`}
          onClick={toggleChat}
        >
          <MessageCircle size={24} style={{ margin: '0 auto', display: 'block' }} />
        </button>
      </div>


      {/* Chat Panel */}
      <div className={`lesson-chat-panel ${theme} ${isChatOpen ? 'open' : ''}`}>
        <div className="lesson-chat-header">
          <h3>AI Chat</h3>
          <div className="lesson-chat-header-actions">
            <button 
              className="lesson-chat-clear-button"
              onClick={handleClearChat}
              disabled={chatMessages.length <= 1 || isChatLoading}
            >
              Clear
            </button>
            <button className="lesson-chat-close-button" onClick={toggleChat}>×</button>
          </div>
        </div>
        <div className={`lesson-chat-messages ${theme}`}>
          {chatMessages.map((message, index) => (
            <div key={index} className={`lesson-chat-message ${message.role}`}>
              {message.role === 'system' && (
                <div className="lesson-chat-message-content">
                  {message.content}
                </div>
              )}
              {message.role === 'user' && (
                <div className="lesson-chat-message-content">
                  {message.content}
                </div>
              )}
              {message.role === 'assistant' && (
                <div className="lesson-chat-message-content">
                  {message.content}
                </div>
              )}
            </div>
          ))}
          {isChatLoading && (
            <div className="lesson-chat-message assistant thinking">
              <span>Thinking</span>
              <span className="lesson-chat-dot">.</span>
              <span className="lesson-chat-dot">.</span>
              <span className="lesson-chat-dot">.</span>
            </div>
          )}
          <div ref={chatMessagesEndRef} />
        </div>
        <form className="lesson-chat-input-container" onSubmit={handleSendMessage}>
          <button 
            type="submit" 
            className="lesson-chat-send-button"
            disabled={!chatInput.trim() || isChatLoading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
          <input
            type="text"
            className="lesson-chat-input"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyDown}
            placeholder="Ask a question..."
            disabled={isChatLoading}
          />
        </form>
      </div>

      {/* Note Creation Form as Modal */}
      {showNoteForm && (
        <div className="lesson-note-modal-overlay" onClick={handleCancelNote}>
          <div className={`lesson-note-modal ${theme}`} onClick={e => e.stopPropagation()}>
            <div className={`lesson-note-modal-header ${theme}`}>
              <h3>Create Note</h3>
              <button 
                className="lesson-note-modal-close"
                onClick={handleCancelNote}
              >
                ×
              </button>
            </div>
            <div className="lesson-note-modal-body">
              <div className="lesson-note-input-group">
                <label className={`lesson-note-input-label ${theme}`}>
                  Title *
                </label>
                <input
                  type="text"
                  className="lesson-note-title-input"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="Enter a title for your note..."
                  autoFocus
                />
              </div>
              <div className="lesson-note-input-group">
                <label className={`lesson-note-input-label ${theme}`}>
                  Content
                </label>
                <textarea
                  className="lesson-note-content-textarea"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Note content... Use [[]] to create links to other notes and #tag to add tags"
                />
                <div className={`lesson-note-textarea-info ${theme}`}>
                  <span>Selected text will be pre-filled</span>
                  <span>{noteContent.length} characters</span>
                </div>
              </div>
            </div>
            <div className={`lesson-note-modal-actions ${theme}`}>
              <button 
                className="lesson-note-cancel-button"
                onClick={handleCancelNote}
              >
                Cancel
              </button>
              <button 
                className="lesson-note-save-button"
                onClick={handleSaveNote}
                disabled={!noteTitle.trim()}
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lesson;